/* eslint-disable @eslint-react/dom/no-dangerously-set-innerhtml */
/* eslint-disable react/no-danger */
import classNames from 'classnames';
import { isString, uniq } from 'lodash';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';
import { getImageAtSize } from '../../lib/utils';

import { useLabels } from '../../contexts/LabelsContext';
import { useThemeClassName } from '../../contexts/ThemeContext';
import ParticipationsStats from './ParticipationsStats';
import Stats from './Stats';

import styles from '../../../styles/partials/game-results.module.scss';

const propTypes = {
    game: AppPropTypes.game,
    participation: AppPropTypes.participation,
    isValid: PropTypes.bool,
    withAnswersCount: PropTypes.bool,
    showGameImage: PropTypes.bool,
    className: PropTypes.string,
    children: PropTypes.node,
};

const defaultProps = {
    game: null,
    participation: null,
    isValid: null,
    withAnswersCount: false,
    showGameImage: false,
    className: null,
    children: null,
};

function GameResults({
    game,
    participation,
    isValid,
    withAnswersCount,
    showGameImage,
    className,
    children,
}) {
    const themeClassName = useThemeClassName(styles);
    const { answers, draws = [] } = participation || {};
    const {
        type,
        questions = null,
        title: gameTitle = null,
        image = null,
        theme = null,
    } = game || {};

    const { singleDrawParticipationsStats = true, showGameParticipationsStats = false } =
        theme || {};

    const {
        gameResultsTitle = null,
        gameResultTitle = null,
        gameResultSubtitle = null,
        gameResultDescription = null,
        gameInvalidResultTitle = null,
        gameInvalidResultSubtitle = null,
        gameInvalidResultDescription = null,
    } = useLabels();

    const defaultResultTitle =
        isValid !== false ? (
            <FormattedMessage
                defaultMessage="Congratulations!"
                description="Game ended result text"
            />
        ) : (
            <FormattedMessage defaultMessage="Almost!" description="Game ended result text" />
        );

    const resultTitle =
        (isValid !== false ? gameResultTitle : gameInvalidResultTitle) || defaultResultTitle;
    const resultSubtitle = isValid !== false ? gameResultSubtitle : gameInvalidResultSubtitle;
    const resultDescription =
        isValid !== false ? gameResultDescription : gameInvalidResultDescription;

    const mediumImage = getImageAtSize(image, 'medium');
    const { url: mediumImageUrl = null } = mediumImage || {};

    const goodAnswersCount = useMemo(
        () => (answers || []).filter(({ valid = false }) => valid).length,
        [answers],
    );

    // const goodAnswersPadded = useMemo(
    //     () => `${goodAnswersCount}`.padStart(2, '0'),
    //     [goodAnswersCount],
    // );

    // console.log('isValid', isValid);

    const { length: questionsCount = 0 } = questions || {};

    const drawsStats = useMemo(() => {
        if (draws === null || draws.length === 0) {
            return null;
        }

        if (singleDrawParticipationsStats) {
            return draws
                .filter(({ participationsCount = 0 }) => participationsCount > 0)
                .slice(0, 1)
                .map((draw) => {
                    const { participationsCount } = draw;
                    return {
                        draw,
                        value: `+${
                            participationsCount < 10
                                ? `${participationsCount}`
                                : participationsCount
                        }`,
                        label:
                            participationsCount > 1 ? (
                                <FormattedMessage
                                    defaultMessage="Additional entries into the draws"
                                    description="Game results stats label"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Additional entry into the draws"
                                    description="Game results stats label"
                                />
                            ),
                    };
                });
        }

        const participationsCounts =
            draws !== null
                ? uniq(
                      draws
                          .map(({ participationsCount = 0 }) => participationsCount)
                          .filter((count) => count > 0),
                  )
                : null;
        const mergeDraws =
            participationsCounts !== null && participationsCounts.length > 1 && false;

        return mergeDraws
            ? [
                  {
                      value: `+${
                          participationsCounts[0] < 10
                              ? `${participationsCounts[0]}`
                              : participationsCounts[0]
                      }`,
                      label:
                          participationsCounts[0] > 1 ? (
                              <FormattedMessage
                                  defaultMessage="Additional entries into the draws"
                                  description="Game results stats label"
                              />
                          ) : (
                              <FormattedMessage
                                  defaultMessage="Additional entry into the draws"
                                  description="Game results stats label"
                              />
                          ),
                  },
              ]
            : draws
                  .filter(({ participationsCount = 0 }) => participationsCount > 0)
                  .map((draw) => {
                      const { title, participationsCount } = draw;
                      return {
                          draw,
                          value: `+${
                              participationsCount < 10
                                  ? `${participationsCount}`
                                  : participationsCount
                          }`,
                          label:
                              participationsCount > 1 ? (
                                  <FormattedMessage
                                      defaultMessage="Additional entries into the {title} draw"
                                      description="Game results stats label"
                                      values={{
                                          title,
                                      }}
                                  />
                              ) : (
                                  <FormattedMessage
                                      defaultMessage="Additional entry into the {title} draw"
                                      description="Game results stats label"
                                      values={{
                                          title,
                                      }}
                                  />
                              ),
                      };
                  });
    }, [draws, singleDrawParticipationsStats]);

    const hasQuestions = questions !== null && questions.length > 1;

    return (
        <div className={classNames([styles.container, styles[type], themeClassName, className])}>
            <div className={styles.box}>
                {gameResultsTitle !== false ? (
                    <h2 className={styles.title}>
                        {gameResultsTitle || (
                            <FormattedMessage
                                defaultMessage="Results"
                                description="Game Results title"
                            />
                        )}
                    </h2>
                ) : null}
                <div className={styles.header}>
                    {showGameImage && mediumImageUrl !== null ? (
                        <img src={mediumImageUrl} alt={gameTitle} className={styles.gameImage} />
                    ) : null}
                    <div className={styles.headerText}>
                        {resultTitle !== false ? (
                            <h4
                                className={styles.resultTitle}
                                dangerouslySetInnerHTML={
                                    isString(resultTitle) ? { __html: resultTitle } : null
                                }
                            >
                                {!isString(resultTitle) ? resultTitle : null}
                            </h4>
                        ) : null}
                        {resultSubtitle !== null ? (
                            <div
                                className={styles.resultSubtitle}
                                dangerouslySetInnerHTML={
                                    isString(resultSubtitle) ? { __html: resultSubtitle } : null
                                }
                            >
                                {!isString(resultSubtitle) ? resultSubtitle : null}
                            </div>
                        ) : null}
                        {resultDescription !== null ? (
                            <div
                                className={styles.resultDescription}
                                dangerouslySetInnerHTML={
                                    isString(resultDescription)
                                        ? { __html: resultDescription }
                                        : null
                                }
                            >
                                {!isString(resultDescription) ? resultDescription : null}
                            </div>
                        ) : null}
                        {hasQuestions && withAnswersCount ? (
                            <div className={styles.goodAnswers}>
                                <div className={styles.goodAnswersCount}>{goodAnswersCount}</div>
                                <div className={styles.goodAnswersLabel}>
                                    <FormattedMessage
                                        defaultMessage="Right answers out of {count}"
                                        description="Game Results good answers label"
                                        values={{ count: questionsCount }}
                                    />
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>

                {children !== null ? children : null}
                {drawsStats !== null ? (
                    <Stats
                        stats={drawsStats}
                        className={styles.stats}
                        valueClassName={styles.value}
                        labelClassName={styles.label}
                    />
                ) : null}
            </div>

            {showGameParticipationsStats ? (
                <ParticipationsStats
                    className={styles.participationsStats}
                    valueClassName={styles.participationsStatsLabel}
                />
            ) : null}

            {/* {drawsStats !== null && hasQuestions ? (
                <Stats
                    stats={drawsStats}
                    className={classNames([styles.box, styles.stats])}
                    labelClassName={styles.label}
                />
            ) : null} */}
        </div>
    );
}

GameResults.propTypes = propTypes;
GameResults.defaultProps = defaultProps;

export default GameResults;
